/**
 * Shared spacing, corner radii, and text tokens for onboarding-style UI
 * (web onboarding kit, patient flows, narrow columns). Does not duplicate palette values:
 * import `colors` from `./colors` next to these tokens in consuming files.
 *
 * `fontFamily`, `headingLevel`, `text`, and `textSize` mirror the legacy `stringStyles.ts`
 * scale (services components, modals, inputs). Prefer these tokens + uiLab typography
 * components for new work; migrate call sites off `stringStyles` over time.
 *
 * For native text fields, use `text.formControl` (minimum 16px computed size) so iOS Safari
 * does not zoom the viewport on focus; `text.body` stays smaller on some breakpoints by design.
 */

/**
 * Fluid spacing: each step is `clamp(min, intercept + slope·vw, max)` so gaps and padding
 * scale gently with viewport while preserving a roughly doubling ladder (xs → xl).
 * Midpoints sit near the legacy fixed rem values around ~375px width; caps avoid runaway padding on wide screens.
 */
export const space = {
  xxxs: 'clamp(0.125rem, 0.11rem + 0.1vw, 0.25rem)',
  xxs: 'clamp(0.1875rem, 0.17rem + 0.1vw, 0.3125rem)',
  xs: 'clamp(0.3125rem, 0.27rem + 0.22vw, 0.4375rem)',
  sm: 'clamp(0.5rem, 0.45rem + 0.42vw, 0.8125rem)',
  md: 'clamp(0.75rem, 0.68rem + 0.72vw, 1.1875rem)',
  lg: 'clamp(1.1875rem, 1rem + 1vw, 1.75rem)',
  xl: 'clamp(1.625rem, 1.35rem + 1.4vw, 2.5rem)',
  xxl: 'clamp(2rem, 1.65rem + 1.75vw, 3.25rem)',
  xxxl: 'clamp(2.5rem, 2.15rem + 2vw, 4rem)',
} as const;

export const radii = {
  sm: '4px',
  /** Interactive controls and cards — Figma `Radius S` (ERS-61). */
  control: '6px',
  md: '8px',
  lg: '12px',
  pill: '9999px',
} as const;

/** Default max width for narrow, mobile-first onboarding columns. */
export const maxOnboardingContentWidth = 'min(100%, calc(32rem + 12vw))';//'min(100%, 36rem)';

/**
 * Bottom inset for scrollable onboarding (or similar) columns when a fixed bottom bar
 * (primary actions, progress, safe area) would cover in-flow content.
 */
export const onboardingFooterScrollReserve = `calc(6.25rem + env(safe-area-inset-bottom, 0px))`;

/** Font stacks aligned with legacy `stringStyles.ts` (Lora / Ubuntu / Barlow / Ubuntu Mono). */
export const fontFamily = {
  lora: '"Lora", Georgia, "Times New Roman", serif',
  ubuntu: '"Ubuntu", system-ui, -apple-system, sans-serif',
  barlow: '"Barlow", "Helvetica Neue", Arial, sans-serif',
  ubuntuMono: '"Ubuntu Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
} as const;

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Semantic `H1`–`H6` scale: font family, weight, and letter-spacing from legacy `stringStyles.ts`,
 * with subtle intrinsic `clamp()` for `font-size` and `line-height` (gentler `vw` terms than body copy).
 * Used by `HeadingLevels` (`H1`–`H6`) in the onboarding uiLab.
 */
export const headingLevel: Record<
  HeadingLevel,
  {
    fontFamily: string;
    fontWeight: number;
    letterSpacing: string;
    fontSize: string;
    lineHeight: string;
  }
> = {
  1: {
    fontFamily: fontFamily.lora,
    fontWeight: 700,
    letterSpacing: '0',
    fontSize: 'clamp(1.25rem, 2.75vw + 0.5rem, 1.75rem)',
    lineHeight: 'clamp(1.625rem, 2.5vw + 1rem, 2.125rem)',
  },
  2: {
    fontFamily: fontFamily.lora,
    fontWeight: 500,
    letterSpacing: '0.02em',
    fontSize: 'clamp(1.125rem, 2.25vw + 0.45rem, 1.5rem)',
    lineHeight: 'clamp(1.75rem, 2vw + 1.1rem, 2.125rem)',
  },
  3: {
    fontFamily: fontFamily.ubuntu,
    fontWeight: 500,
    letterSpacing: '0.02em',
    fontSize: 'clamp(1.0625rem, 1.75vw + 0.4rem, 1.125rem)',
    lineHeight: 'clamp(1.3125rem, 1.5vw + 0.75rem, 1.5rem)',
  },
  4: {
    fontFamily: fontFamily.ubuntu,
    fontWeight: 500,
    letterSpacing: '0',
    fontSize: 'clamp(0.9375rem, 1.25vw + 0.5rem, 1rem)',
    lineHeight: 'clamp(1.375rem, 1.25vw + 0.85rem, 1.625rem)',
  },
  5: {
    fontFamily: fontFamily.ubuntu,
    fontWeight: 400,
    letterSpacing: '0',
    fontSize: 'clamp(0.9375rem, 1vw + 0.55rem, 1rem)',
    lineHeight: 'clamp(1.3125rem, 1vw + 0.9rem, 1.625rem)',
  },
  6: {
    fontFamily: fontFamily.ubuntu,
    fontWeight: 600,
    letterSpacing: '0',
    fontSize: 'clamp(0.8125rem, 0.85vw + 0.5rem, 0.875rem)',
    lineHeight: 'clamp(1.125rem, 0.85vw + 0.65rem, 1.25rem)',
  },
};

/**
 * Step scale for copy: `xxxs`–`l` are fixed rem (max font-size under ~18px); `xl` and `xxl` use
 * intrinsic `clamp()` from 1rem upward (18px / 20px caps). Aligns with legacy `stringStyles` Body /
 * Small / Mini / caption / overline sizes converted to rem.
 */
export const textSize = {
  xxxs: { fontSize: '0.6875rem', lineHeight: '1rem' },
  xxs: { fontSize: '0.75rem', lineHeight: '1rem' },
  xs: { fontSize: '0.8125rem', lineHeight: '1.15rem' },
  s: { fontSize: '0.875rem', lineHeight: '1.25rem' },
  m: { fontSize: '0.9375rem', lineHeight: '1.625rem' },
  l: { fontSize: '1rem', lineHeight: '1.625rem' },
  xl: {
    fontSize: 'clamp(1rem, 1.2vw + 0.7rem, 1.125rem)',
    lineHeight: 'clamp(1.25rem, 1.2vw + 0.9rem, 1.5rem)',
  },
  xxl: {
    fontSize: 'clamp(1.0625rem, 1.65vw + 0.5rem, 1.25rem)',
    lineHeight: 'clamp(1.375rem, 1.5vw + 0.95rem, 1.625rem)',
  },
} as const;

export type TextSizeKey = keyof typeof textSize;

/** Semantic copy roles (Ubuntu document scale + kit body). Use `textSize` for raw steps. */
export const text = {
  overline: { ...textSize.xxxs, fontWeight: 400 as const },
  mini: { ...textSize.xxs, fontWeight: 400 as const },
  caption: { ...textSize.xs, fontWeight: 400 as const },
  small: { ...textSize.s, fontWeight: 400 as const },
  body: { ...textSize.m, fontWeight: 400 as const },
  /**
   * Native text fields (`input`, `textarea`, …): same rhythm as `body`, but computed `font-size`
   * is never below 16px so iOS Safari does not zoom the viewport when the control is focused.
   */
  formControl: {
    fontSize: 'max(16px, 0.9375rem)',
    lineHeight: textSize.m.lineHeight,
    fontWeight: 400 as const,
  },
  button: {
    fontSize: textSize.m.fontSize,
    lineHeight: '1.25rem',
    fontWeight: 500 as const,
  },
} as const;

export type TextRoleKey = keyof typeof text;

/** Legacy `Numbers` style (Barlow). */
export const documentNumericText = {
  fontFamily: fontFamily.barlow,
  fontSize: '1rem',
  lineHeight: '1.625rem',
  fontWeight: 700 as const,
} as const;

/** Legacy `MonoText` (Ubuntu Mono, medium color — apply `colors.medium` in the component). */
export const documentMonospaceText = {
  fontFamily: fontFamily.ubuntuMono,
  fontSize: '1rem',
  lineHeight: '1.625rem',
  fontWeight: 400 as const,
} as const;

/**
 * Hyphenation for running body copy. German compounds ("Teilnahmebescheinigung",
 * "Bonusprogramms") otherwise push whole words to the next line and leave a badly ragged edge in
 * the narrow onboarding column.
 *
 * Relies on `<html lang="de">` — the browser picks its hyphenation dictionary from the language,
 * so this must not be applied to text in another language without setting `lang` on it.
 *
 * `hyphenate-limit-chars` keeps short words intact and avoids one- or two-letter fragments; where
 * a browser does not support it the text still hyphenates, just with the browser's own limits.
 * Deliberately NOT applied to headings, list-item titles, buttons, inputs, or numeric/mono text.
 */
export const documentHyphenation = `
  hyphens: auto;
  -webkit-hyphens: auto;
  hyphenate-limit-chars: 7 3 3;
`;
